import { getRTFInformation } from "@bond-london/graphcms-rich-text";
import classNames from "classnames";
import React, {
  Fragment,
  cloneElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import { PagesContext } from "../contexts/PagesContext";
import { Separator } from "../elements/Separator";
import { MainWidthClassName, SectionPaddingClassName } from "../styles";
import { buildThumbnailLinkFromPage, filterPages } from "./BlockCollection";
import {
  getAlt,
  getImageForPage,
  getLinkForPage,
  getPageCategory,
} from "./Blocks";
import { CollectionProps } from "./Collection";
import { PageFilter } from "./PageFilter";
import { PageLink } from "./PageLink";

const getThumbailSizeFromIndex = (
  index: number,
  order: string | undefined | null,
) => {
  const mod = index % 3;
  switch (mod) {
    case 0:
      return order == "Desc" ? "LARGE" : "SMALL";
    case 1:
      return "MEDIUM";
    default:
      return order == "Desc" ? "SMALL" : "LARGE";
  }
};

function usePageFiltering(
  pageTypeFilter?: Queries.GraphCMS_PageType | null,
  pageFilterCount?: number | null,
  showLargeFirstResult?: boolean | null,
): [
  (c: Queries.GraphCMS_Category | undefined) => void,
  Queries.DirectPageFragment | undefined,
  Array<Queries.DirectPageFragment> | undefined,
] {
  const { cmsPages } = useContext(PagesContext);
  const [categoryFilter, setCategoryFilter] =
    useState<Queries.GraphCMS_Category>();
  const reallySetCategoryFilter = useCallback(
    (filter: string | undefined) =>
      setCategoryFilter(
        filter === "*" ? undefined : (filter as Queries.GraphCMS_Category),
      ),
    [],
  );
  const initialFilter = useMemo(
    () =>
      pageTypeFilter && pageFilterCount
        ? filterPages(cmsPages, pageTypeFilter, pageFilterCount)
        : undefined,
    [cmsPages, pageTypeFilter, pageFilterCount],
  );
  const filteredPages = useMemo(
    () =>
      categoryFilter && initialFilter
        ? initialFilter.filter((p) => p.pageCategory.includes(categoryFilter))
        : initialFilter,
    [categoryFilter, initialFilter],
  );

  const summaryPage =
    filteredPages && showLargeFirstResult ? filteredPages[0] : undefined;

  const remainingPages = useMemo(
    () => (summaryPage ? filteredPages?.slice(1) : filteredPages),
    [summaryPage, filteredPages],
  );

  return [reallySetCategoryFilter, summaryPage, remainingPages];
}

export const PageCollection: React.FC<CollectionProps> = ({
  title,
  showTitle,
  contents,
  imageSizing,
  archiveLink,
  className,
  pageFilterCount,
  pageTypeFilter,
  showLargeFirstResult,
  showFilter,
  shape,
}) => {
  const { t } = useTranslation();
  const [setCategoryFilter, summaryPage, remainingPages] = usePageFiltering(
    pageTypeFilter,
    pageFilterCount,
    showLargeFirstResult,
  );

  const realContents = remainingPages
    ? remainingPages.map(buildThumbnailLinkFromPage)
    : contents.map(({ content }) => content);

  return (
    <section
      data-component="Page Collection"
      className={classNames(
        SectionPaddingClassName,
        "page-grid",
        "relative",
        "max-width",
        className,
      )}
    >
      {showFilter && (
        <PageFilter
          filterText={t("Filter by...")}
          shape={shape}
          onFilterChanged={setCategoryFilter}
        />
      )}
      {summaryPage ? (
        <PageLink
          className={classNames(
            "col-span-4 col-start-2 md:col-span-8 md:col-start-2 lg:col-span-12 lg:col-start-3",
          )}
          key={summaryPage.id}
          image={getImageForPage(summaryPage)}
          alt={getAlt(summaryPage.heroImage, summaryPage.title)}
          title={summaryPage.title}
          heading={getPageCategory(summaryPage)}
          message={getRTFInformation(summaryPage.summary)}
          link={getLinkForPage(summaryPage)}
          linkTitle={summaryPage.linkTitle}
        />
      ) : (
        pageTypeFilter &&
        !!pageFilterCount && (
          <h2 className={classNames("h3", MainWidthClassName)}>
            <Trans>No results</Trans>
          </h2>
        )
      )}
      {showTitle && title && (
        <h2
          className={classNames(
            "relative",
            "h3",
            "mt-20px",
            "mb-60px",
            "lg:mb-96px",
            MainWidthClassName,
            "text-center",
          )}
        >
          {title}
        </h2>
      )}
      {!!realContents?.length && (
        <ul
          className={classNames(
            "grid gap-x-6 gap-y-6 md:gap-y-12",
            MainWidthClassName,
            "grid-cols-3 md:col-start-2 md:grid-cols-6 lg:grid-cols-9",
          )}
        >
          {realContents.map((content, i) => {
            return (
              <Fragment key={content.key}>
                {i % 3 === 0 && (
                  <Separator
                    className={classNames(
                      "hidden md:col-span-full md:col-start-1 md:block",
                    )}
                    key={`separator-${i}`}
                  />
                )}
                <li className={classNames("col-span-3")}>
                  {cloneElement(content, {
                    imageSize: getThumbailSizeFromIndex(i, imageSizing),
                  })}
                </li>
              </Fragment>
            );
          })}
        </ul>
      )}
      {archiveLink && (
        <div
          className={classNames(
            "text-right",
            "col-start-2",
            "col-span-4",
            "my-20px",
            "lg:mt-96px",
            "lg:col-start-3",
            "lg:col-span-12",
          )}
        >
          {archiveLink}
        </div>
      )}
    </section>
  );
};
